<template>
  <div class="directory-root">
    <div class="is-flex-tablet">
      <!-- Magnifier for this panel only. Garrett, 2026-09-16: "sometimes it's very hard to read this.
           All of our font throughout live is... because the monitors have gotten higher res, are
           getting smaller and smaller, and people are getting older... you just put a magnifier on
           it". A lens that follows the cursor, switched on from the button on the Search People label.
           He pitched it as a Live-wide function; scoped here for now on Craig's instruction. -->
      <div
        ref="magnifiedPanel"
        class="modal-card call-log"
        @mousemove="onMagnifierMove"
        @mouseleave="hideLens"
        @scroll.capture="scheduleLensRebuild">
        <div class="view-container box mb-5">
          <div class="columns is-multiline is-mobile box">
            <div class="column is-12">
              <div class="field">
                <label class="label">
                  Search People
                  <span class="is-pulled-right directory-magnifier">
                    <a class="button is-small" :class="{'is-info': magnifierOn}" @click="toggleMagnifier" v-tooltip="magnifierOn ? 'Turn the magnifier off' : 'Magnify whatever the cursor is over'">
                      <b-icon pack="fas" icon="search-plus" size="is-small"></b-icon>
                    </a>
                  </span>
                </label>
                <div class="control is-fullwidth">
                  <!-- Driven from the keyboard without reaching for the mouse - Garrett, 2026-09-16:
                       "the problem is switching between mouse and keyboard is very cumbersome".
                       Down/Up walk the results, Enter takes the highlighted one (the first, until
                       Down has been pressed), Escape clears the query. .prevent on Down/Up stops the
                       caret jumping to the ends of the text while walking the list. -->
                  <b-autocomplete
                    ref="searchInput"
                    tabindex="3"
                    :data="[]"
                    v-model="searchQuery"
                    :expanded="true"
                    :clearable="true"
                    @keydown.native.down.prevent="moveResultHighlight(1)"
                    @keydown.native.up.prevent="moveResultHighlight(-1)"
                    @keydown.native.enter="selectHighlightedResult"
                    @keydown.native.esc="searchQuery = ''"
                    placeholder="Name, email, mobile, department, or dealer">
                  </b-autocomplete>
                </div>
              </div>
            </div>
          </div>
          <div class="columns is-mobile" v-if="selectedAgent !== null">
            <div class="column is-12 box">
              <section-header
                title="Contact Details"
                :collapsed="contactDetailsCollapsed"
                v-on:collapsed-status-changed="onContactDetailsCollapsedChanged">
              </section-header>
              <div class="columns is-multiline">
                <div class="column is-4">
                  <div class="field">
                    <label class="label">Name</label>
                    <div class="field">
                      {{ selectedAgent.fullName || ((selectedAgent.firstName || '') + ' ' + (selectedAgent.lastName || '')) }}
                      <span v-if="selectedAgent.dealerName" class="has-text-grey"> — {{ selectedAgent.dealerName }}</span>
                    </div>
                  </div>
                  <div class="field">
                    <label class="label">Email</label>
                    <div class="field">
                      <span class="clickable" v-clipboard:copy="selectedAgent.email" @click.stop.prevent="onCopy('email', selectedAgent.email)">{{ selectedAgent.email }}</span>
                    </div>
                  </div>
                </div>
                <div class="column is-4">
                  <div class="field">
                    <label class="label">Telephone</label>
                    <div class="field">
                      <click-to-call :destinationLineProp="selectedAgent.telephone" :destinationNameProp="selectedAgent.fullName" :receiverTypeProp="'User'"></click-to-call>
                    </div>
                  </div>
                  <div class="field">
                    <label class="label">Mobile</label>
                    <div class="field">
                      <click-to-call :destinationLineProp="selectedAgent.mobile" :destinationNameProp="selectedAgent.fullName" :receiverTypeProp="'User'"></click-to-call>
                    </div>
                  </div>
                </div>
                <div class="column is-4">
                  <div class="field">
                    <label class="label">Tracking Number</label>
                    <div class="field">
                      {{ selectedAgent.callTrackingNumber }}
                    </div>
                  </div>
                  <div class="field">
                    <label class="label">Extension</label>
                    <div class="field">
                      {{ selectedAgent.extension }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="columns is-multiline is-mobile">
            <div id="browse-panel" class="column is-12 box">
              <section-header
                title="Browse by department"
                :collapsed="browsePanelCollapsed"
                v-on:collapsed-status-changed="onBrowsePanelCollapsedChanged"
                parentSelector="#browse-panel">
              </section-header>
              <div class="columns">
                <div class="column is-12">
                  <!-- tabIndex goes to -1 while this panel is rolled up. "Collapsed" here is
                       height:45px + overflow:hidden (.header-collapsed in main.scss), so the contents
                       are only CLIPPED - they stay in the DOM and stay tabbable. Tabbing out of the
                       search box therefore landed on department buttons nobody could see, and the
                       panel scrolled itself open - Garrett, 2026-09-16: "Tab is gonna go open the
                       browse departments, watch... And that's hidden. And it's weird." -->
                  <button-list-selector
                    :tabIndex="browsePanelCollapsed ? -1 : 4"
                    class="column is-12"
                    validateAs="contact type"
                    label="Contact Type"
                    labelName="name"
                    v-model="selectedDepartment"
                    :additionalDisplayOptions="departmentExtraDisplayOption"
                    :buildTitle="buildDepartmentTitle"
                    :availableValues="availableDepartments">
                    <!-- The department number is the one worth dialling straight from the directory -
                         a person's own numbers stay as they were, in their contact box. Rendered as
                         the click-to-call component rather than the coloured string the default slot
                         produces, so the number here is actually actionable. The extension keeps the
                         same "/ EXT. 596" treatment beside it. -->
                    <template v-slot:selected-extras="{ item }">
                      <click-to-call
                        v-if="item.telephone"
                        :destinationLineProp="item.telephone"
                        :destinationNameProp="item.name"
                        :receiverTypeProp="'User'">
                      </click-to-call>
                      <template v-if="item.extension">
                        <span class="dir-ext-label"> / EXT. </span><span class="dir-dialable">{{ item.extension }}</span>
                      </template>
                    </template>
                  </button-list-selector>
                  <button-list-selector
                    ref="agentSelector"
                    :key="agentSelectorInstanceId"
                    :tabIndex="browsePanelCollapsed ? -1 : 5"
                    :showFilter="true"
                    :showAllToggle="false"
                    :showGroupToggle="false"
                    labelName="fullName"
                    v-model="selectedAgent"
                    class="column is-12"
                    validateAs="agent"
                    label="Agent"
                    :emptyText="agentSelectorEmptyText"
                    :useFilterPlaceholder="'Enter a persons name or a department name to filter'"
                    :filterFunction="agentFilterFunction"
                    :additionalDisplayOptions="agentExtraDisplayOption"
                    :selectedIsAvailable="selectedAgentAvailable"
                    :buildTitle="buildButtonTitle"
                    :availableValues="availableAgents"
                    v-on:on-change="onSelectedAgent">
                  </button-list-selector>
                </div>
              </div>
            </div>
          </div>
          <div class="columns is-multiline is-mobile" v-if="recentContacts.length">
            <div id="recent-contacts" class="column is-12 box">
              <section-header
                title="Recent Contacts"
                v-on:collapsed-status-changed="onShowRecentContacts"
                :collapsed="!recentContactsOpen"
                parentSelector="#recent-contacts">
              </section-header>
              <div class="card-header" role="button">
                <div class="card-header-title is-size-7 stacked-heading">
                  <span>Last {{ recentContactLimit }} people you've called or received calls from</span>
                  <span v-if="selectedAgent" class="selected-value-display">{{ selectedAgent.fullName }} <span v-html="agentExtraDisplayOption(selectedAgent, 'value')"></span></span>
                </div>
              </div>
              <div class="card-content">
                <b-button
                  v-for="user in recentContacts"
                  :key="user.id"
                  :class="{ 'boption': true, 'button': true, 'is-info': selectedAgent && selectedAgent.id === user.id }"
                  @click="onRowSelected(user)"
                  v-tooltip="recentContactTooltip(user)">
                  <b-icon
                    v-if="user.isAvailable"
                    pack="fa"
                    icon="check-circle"
                    type="is-success">
                  </b-icon>
                  <b-icon
                    v-else
                    pack="fa"
                    icon="times-circle"
                    type="is-danger">
                  </b-icon>
                  <span>{{ user.fullName }}</span>
                </b-button>
              </div>
            </div>
          </div>
          <div class="columns is-multiline is-mobile">
            <div id="search-results" class="column is-12 box">
              <section-header
                title="Results"
                v-on:collapsed-status-changed="onShowSearchResults"
                :collapsed="!searchResultsOpen"
                parentSelector="#search-results">
              </section-header>
              <div class="card-header" role="button">
                <p class="card-header-title is-size-7">
                  People search results
                  <span v-if="selectedAgent" class="selected-value-display is-pulled-right" style="padding-left: 10px;">{{ selectedAgent.fullName }} <span v-html="agentExtraDisplayOption(selectedAgent, 'value')"></span></span>
                </p>
              </div>
              <div class="card-content">
                <b-table
                  :data="filteredResults"
                  :row-class="resultRowClass"
                  :selected.sync="selectedAgent"
                  :height="'auto'"
                  :sticky-header="true"
                  :mobile-cards="true"
                  :narrowed="true"
                  @select="onRowSelected"
                  @dblclick="onRowSelected"
                  :focusable="true">
                  <template slot-scope="props">
                    <b-table-column field="fullName" v-tooltip="props.row.fullName" label="Name">
                      {{ (props.row.firstName + ' ' + props.row.lastName) | truncate(20) }}
                    </b-table-column>
                    <b-table-column field="departmentName" v-tooltip="props.row.departmentName" label="Department">
                      {{ props.row.departmentName | truncate(20) }}
                    </b-table-column>
                    <b-table-column field="dealerName" v-tooltip="props.row.dealerName" label="Dealer">
                      {{ props.row.dealerName | truncate(20) }}
                    </b-table-column>
                    <!-- One column, "number / EXT. 596", rather than a bare Ext column ahead of the
                         number. Same builder as the button lists so the two read identically. -->
                    <b-table-column field="telephone" v-tooltip="props.row.telephone || props.row.mobile" label="Phone">
                      <span v-html="buildContactDisplay(props.row.telephone || props.row.mobile, props.row.extension)"></span>
                    </b-table-column>
                    <b-table-column field="isAvailable" label="Available" centered>
                      <b-icon
                        v-if="props.row.isAvailable"
                        pack="fa"
                        icon="check-circle"
                        type="is-success"
                        v-tooltip="'Available'">
                      </b-icon>
                      <b-icon
                        v-else
                        pack="fa"
                        icon="times-circle"
                        type="is-danger"
                        v-tooltip="props.row.notAvailableReason || 'Unavailable'">
                      </b-icon>
                    </b-table-column>
                  </template>
                  <template slot="empty">
                    <section class="section">
                      <div class="has-text-centered">
                        <p><b-icon icon="emoticon-sad" size="is-large"></b-icon></p>
                        <p v-if="searchQuery">No people match "{{ searchQuery }}"</p>
                        <p v-else>Type a name, email, mobile, department, or dealer to search</p>
                      </div>
                    </section>
                  </template>
                </b-table>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- The lens holds a scaled copy of the panel, positioned so the point under the cursor sits in
           its centre. It lives OUTSIDE the panel so the panel's MutationObserver never sees the lens
           moving, and inside the same ancestors so the copy picks up the same styles. inert keeps the
           copy's inputs and buttons out of the Tab order; pointer-events:none lets the mouse through. -->
      <div
        v-show="magnifierOn"
        ref="lens"
        class="directory-lens"
        inert
        aria-hidden="true">
        <div ref="lensContent" class="directory-lens-content"></div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import SectionHeader from '@/components/generic/SectionHeader'
import CompanyDirectoryButtonListSelector from '@/components/generic/CompanyDirectoryButtonListSelector'
import ClickToCallComponent from '@/components/generic/ClickToCallComponent.vue'
import userService from '@/services/userService'
import utilities from '@/mixins/generic/utilities'

import { orderBy } from 'es-toolkit'
import { VTooltip, VClosePopover } from 'v-tooltip'
import { createNamespacedHelpers } from 'vuex'
const mapUserGetters = createNamespacedHelpers('user').mapGetters
const mapDealerState = createNamespacedHelpers('dealer').mapState
const mapDealerGetters = createNamespacedHelpers('dealer').mapGetters

export default {
  name: 'CompanyDirectoryComponent',
  props: {
    inFocus: {
      type: Boolean,
      default: false
    }
  },
  mixins: [utilities],
  directives: {tooltip: VTooltip, 'close-popover': VClosePopover},
  components: {
    'section-header': SectionHeader,
    'button-list-selector': CompanyDirectoryButtonListSelector,
    'click-to-call': ClickToCallComponent
  },
  data () {
    return {
      selectedDepartment: null,
      selectedAgent: null,
      searchQuery: '',
      recentContacts: [],
      recentContactLimit: 100,
      recentContactsOpen: false,
      searchResultsOpen: false,
      contactDetailsCollapsed: false,
      browsePanelCollapsed: true,
      //-1 means "nothing walked to yet", which is what makes Enter take the first result.
      highlightedResultIndex: -1,
      magnifierOn: false,
      agentSelectorInstanceId: this.$uuid.v4()
    }
  },
  computed: {
    ...mapUserGetters(['allUsers', 'currentUser']),
    ...mapDealerState(['currentDealer']),
    ...mapDealerGetters(['allDepartments']),
    availableDepartments: function () {
      let popAvailableDepartments = this.allDepartments.filter((x) => x.dealerId === this.currentDealer.id && x.name !== '').map((y) => {
        return {
          ...y,
          refId: y.id,
          popName: y.popName
        }
      })
      return orderBy([...popAvailableDepartments], ['position'], ['asc'])
    },
    agentSelectorEmptyText: function () {
      // Only once a department is chosen - with none selected an empty agent
      // list just means allUsers has not loaded yet, which is not "no agents".
      return this.selectedDepartment ? 'No agents in department' : ''
    },
    availableAgents: function () {
      let allPeople = orderBy(this.allUsers, (u) => [(u.firstName || '').toLowerCase()], ['asc'])
      if (!this.selectedDepartment) {
        return [...allPeople]
      }
      return [...allPeople.filter((y) => y.departmentId === this.selectedDepartment.id)]
    },
    filteredResults: function () {
      if (!this.searchQuery || !this.searchQuery.trim()) return []
      return this.allUsers.filter((u) => this.agentFilterFunction(u, this.searchQuery.trim()))
    },
  },
  methods: {
    availabilityStatus: function (user) {
      if (user.id === 0 || user.isAvailable) {
        return ''
      }
      return ' is currently unavailable - ' + (user.notAvailableReason || '')
    },
    onSelectedAgent: function (agent) {
      this.selectedAgent = null
      if (!agent || agent === null) {
        return
      }
      this.selectedAgent = agent
      // A manual collapse leaves the header's own isCollapsed true while the
      // prop sits at false, so re-asserting false would be a no-op. Tracking
      // the state here means this flips true -> false and the watcher fires.
      this.contactDetailsCollapsed = false
      // Contact Details renders above the browse panel, so a selection made
      // further down (recent contacts, results table) would otherwise leave
      // the newly shown detail off-screen. Wait for the v-if to render first.
      this.$nextTick(() => {
        let container = this.$el.querySelector('.view-container')
        if (container && container.scrollIntoView) {
          container.scrollIntoView({behavior: 'smooth', block: 'start'})
        }
      })
    },
    onRowSelected: function (row) {
      this.onSelectedAgent(row)
    },
    toggleMagnifier: function () {
      this.magnifierOn = !this.magnifierOn
      if (!this.magnifierOn) {
        this.stopMagnifier()
        return
      }
      //Tooltips pop up over whatever the lens is reading - Craig, 2026-09-18: "when using the zoom,
      //need to remove tooltips from anything you hover over". v-tooltip mounts them on body, outside
      //this component, so they are hidden by a body class rather than a scoped rule.
      document.body.classList.add('directory-magnifier-on')
      //After the v-show has rendered the lens, so the copy is laid out and its scroll offsets stick.
      this.$nextTick(() => {
        this.rebuildLens()
        //The copy is a snapshot, so anything that changes the panel - typing, a selection, a panel
        //opening - has to be re-copied or the lens would magnify a stale picture.
        this.lensObserver = new MutationObserver(this.scheduleLensRebuild)
        this.lensObserver.observe(this.$refs.magnifiedPanel, {subtree: true, childList: true, characterData: true, attributes: true})
      })
    },
    stopMagnifier: function () {
      document.body.classList.remove('directory-magnifier-on')
      if (this.lensObserver) {
        this.lensObserver.disconnect()
        this.lensObserver = null
      }
      clearTimeout(this.lensRebuildTimer)
      this.hideLens()
      if (this.$refs.lensContent) {
        this.$refs.lensContent.replaceChildren()
      }
    },
    scheduleLensRebuild: function () {
      if (!this.magnifierOn) {
        return
      }
      clearTimeout(this.lensRebuildTimer)
      this.lensRebuildTimer = setTimeout(this.rebuildLens, 100)
    },
    rebuildLens: function () {
      let panel = this.$refs.magnifiedPanel
      let target = this.$refs.lensContent
      if (!panel || !target) {
        return
      }
      let copy = panel.cloneNode(true)
      //cloneNode copies the value ATTRIBUTE, not what has been typed, so the search box would read empty.
      let sourceFields = panel.querySelectorAll('input, textarea, select')
      let copyFields = copy.querySelectorAll('input, textarea, select')
      sourceFields.forEach((field, index) => {
        copyFields[index].value = field.value
      })
      //Same width so it wraps exactly as the real panel does; no margin so its origin is the panel's.
      copy.style.width = panel.offsetWidth + 'px'
      copy.style.margin = '0'
      target.replaceChildren(copy)
      //Scroll offsets only take once the copy is in the document. Ids are stripped afterwards so the
      //index pairing of the two element lists is not disturbed first.
      let sourceElements = panel.querySelectorAll('*')
      let copyElements = copy.querySelectorAll('*')
      sourceElements.forEach((element, index) => {
        if (element.scrollTop || element.scrollLeft) {
          copyElements[index].scrollTop = element.scrollTop
          copyElements[index].scrollLeft = element.scrollLeft
        }
      })
      copy.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'))
    },
    //Styles are written straight to the lens rather than through reactive data - a reactive update on
    //every mousemove would re-render this whole template, results table included.
    onMagnifierMove: function (event) {
      let lens = this.$refs.lens
      if (!this.magnifierOn || !lens) {
        return
      }
      let radius = 110
      let scale = 2
      let panelRect = this.$refs.magnifiedPanel.getBoundingClientRect()
      let rootRect = this.$el.getBoundingClientRect()
      let pointX = event.clientX - panelRect.left
      let pointY = event.clientY - panelRect.top
      lens.style.width = (radius * 2) + 'px'
      lens.style.height = (radius * 2) + 'px'
      lens.style.left = (event.clientX - rootRect.left - radius) + 'px'
      lens.style.top = (event.clientY - rootRect.top - radius) + 'px'
      lens.style.visibility = 'visible'
      //Scaled from the top-left corner, then shifted so the point under the cursor lands on the centre.
      this.$refs.lensContent.style.transform = 'translate(' + (radius - pointX * scale) + 'px, ' + (radius - pointY * scale) + 'px) scale(' + scale + ')'
    },
    hideLens: function () {
      if (this.$refs.lens) {
        this.$refs.lens.style.visibility = 'hidden'
      }
    },
    //Marks the row Down/Up has walked to. Separate from b-table's own :selected because selecting a
    //row collapses the panels and scrolls to Contact Details - that should happen on Enter, not on
    //every arrow press.
    resultRowClass: function (row, index) {
      return index === this.highlightedResultIndex ? 'keyboard-highlight' : ''
    },
    //Walks filteredResults from the search box. Clamped rather than wrapped: running off the end of
    //a long list and reappearing at the top is disorientating when the list is not all on screen.
    moveResultHighlight: function (step) {
      let results = this.filteredResults
      if (!results.length) {
        return
      }
      let next = this.highlightedResultIndex + step
      if (next < 0) {
        next = 0
      }
      if (next > results.length - 1) {
        next = results.length - 1
      }
      this.highlightedResultIndex = next
      //Keep the highlighted row in view - the results table is a sticky-header scroller, so walking
      //past the fold otherwise moves a selection the operator cannot see.
      this.$nextTick(() => {
        let rows = this.$el.querySelectorAll('#search-results tbody tr')
        let row = rows && rows[this.highlightedResultIndex]
        if (row && row.scrollIntoView) {
          row.scrollIntoView({block: 'nearest'})
        }
      })
    },
    //Enter takes whatever Down/Up landed on, and the first result when neither has been pressed -
    //which is the common case: type enough to narrow it to one, then Enter. Garrett, 2026-09-16:
    //"enter's not working. Enter should work."
    selectHighlightedResult: function () {
      let results = this.filteredResults
      if (!results.length) {
        return
      }
      let index = this.highlightedResultIndex >= 0 && this.highlightedResultIndex < results.length
        ? this.highlightedResultIndex
        : 0
      this.onSelectedAgent(results[index])
    },
    onContactDetailsCollapsedChanged: function (isCollapsed) {
      this.contactDetailsCollapsed = isCollapsed
    },
    onShowRecentContacts: function (isCollapsed) {
      this.recentContactsOpen = !isCollapsed
    },
    onShowSearchResults: function (isCollapsed) {
      this.searchResultsOpen = !isCollapsed
    },
    onBrowsePanelCollapsedChanged: function (isCollapsed) {
      this.browsePanelCollapsed = isCollapsed
    },
    recentContactTooltip: function (user) {
      let parts = [user.fullName || '']
      if (user.departmentName) parts.push(user.departmentName)
      if (user.dealerName) parts.push(user.dealerName)
      return parts.filter((p) => p !== '').join(' — ')
    },
    loadRecentContacts: async function () {
      if (!this.currentUser || !this.currentUser.id) return
      try {
        let response = await userService.findLastUserContactByUser(this.currentUser.id, this.recentContactLimit)
        let data = (response && response.results) || []
        this.recentContacts = data.map((x) => ({ ...x }))
        //Open on arrival when there is nothing typed yet. They load asynchronously, so the panel is
        //rendered long before there is anything in it - Garrett, 2026-09-16: "by default, I have a
        //recent, it doesn't show the results open, and I think they should open". A query already
        //typed means the operator is searching, and the searchQuery watcher owns the panels then.
        if (this.recentContacts.length && !(this.searchQuery || '').trim()) {
          this.recentContactsOpen = true
        }
      } catch (error) {
        console.log('Error loading recent user contacts:', error)
      }
    },
    buildButtonTitle: function (item) {
      if (item.isAvailable) {
        return ' is online and available'
      } else {
        return ' is currently unavailable - ' + item.notAvailableReason
      }
    },
    //EVERY term must match something, not any of them. This used to OR the terms, so "Steven Bruno"
    //returned every Steven and every Bruno, and because the fields searched include the department
    //and the dealer, a person's name could match a department - which is how searching for a person
    //produced "Ferrari Sav" (Garrett, 2026-09-16: "these matches are not correct matches").
    //Empty terms are dropped before the test. split(' ') on a double space yields '', and
    //''.indexOf('') >= 0 is true for every record, so one stray space matched the entire directory.
    //The callers trim, which hid it for a trailing space but not an internal one - that is why the
    //same search appeared to work and then not work.
    agentFilterFunction: function (x, filter) {
      if (x.id === 0) {
        return false
      }
      let filterElements = (filter || '').toLowerCase().split(' ').filter(element => element !== '')
      if (!filterElements.length) {
        return false
      }
      return filterElements.every(element =>
        (x.firstName || '').toLowerCase().indexOf(element) >= 0 ||
        (x.lastName || '').toLowerCase().indexOf(element) >= 0 ||
        (x.fullName || '').toLowerCase().indexOf(element) >= 0 ||
        (x.email || '').toLowerCase().indexOf(element) >= 0 ||
        this.unmask(x.mobile || '').toLowerCase().indexOf(element) >= 0 ||
        (x.departmentName || '').toLowerCase().indexOf(element) >= 0 ||
        (x.dealerName || '').toLowerCase().indexOf(element) >= 0)
    },
    departmentExtraDisplayOption: function (item, type) {
      if (!item) {
        return ''
      }
      if (type === 'value') {
        return this.buildContactDisplay(item.telephone, item.extension)
      }
      return this.buildDepartmentTitle(item)
    },
    buildDepartmentTitle: function (item) {
      if (!item) {
        return ''
      }
      let result = item.name ? item.name + '\r\n' : ''
      if (item.extension && item.extension !== '') {
        result = result + 'ext: ' + item.extension + '\r\n'
      }
      if (item.telephone && item.telephone !== '') {
        result = result + 'phone: ' + item.telephone + '\r\n'
      }
      if (item.altExtension && item.altExtension !== '') {
        result = result + 'alt ext: ' + item.altExtension + '\r\n'
      }
      if (item.altTelephone && item.altTelephone !== '') {
        result = result + 'alt phone: ' + item.altTelephone + '\r\n'
      }
      return result
    },
    selectedAgentAvailable: function (user) {
      return user && user.isAvailable
    },
    //Contact values are rendered with v-html so the number can be blue and the rest plain, so every
    //piece that comes from the database goes through here first. The fields are short strings from
    //user admin, but they are still user input and this is the only thing standing between them and
    //the DOM.
    escapeHtml: function (value) {
      return String(value === null || value === undefined ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
    },
    //"number / EXT. 596", not "596 - number". The extension used to lead, which made it invisible -
    //Garrett, 2026-09-16: "there's no extension there... I can't see it that way, so I think we need
    //to space it, or maybe put a dash". Only the DIGITS are blue, on both sides: blue reads as
    //"dialable" the way it does on a phone, so the separator and the word EXT stay plain to avoid
    //implying you can click them ("the number becomes a blue, and then the clickable, and then
    //there's no color in the dash, and then the extension isn't blue, but the number 596 is blue").
    buildContactDisplay: function (phone, extension) {
      let parts = []
      if (phone && phone !== '') {
        parts.push('<span class="dir-dialable">' + this.escapeHtml(phone) + '</span>')
      }
      if (extension && extension !== '') {
        parts.push('<span class="dir-ext-label">EXT.</span> <span class="dir-dialable">' + this.escapeHtml(extension) + '</span>')
      }
      return parts.join('<span class="dir-ext-label"> / </span>')
    },
    agentExtraDisplayOption: function (item, type) {
      var result = ''
      if (type === 'value') {
        return this.buildContactDisplay(item.telephone || item.mobile, item.extension)
      } else {
        if (item.extension && item.extension !== '') {
          result = 'ext: ' + item.extension + '\r\n'
        }
        if (item.telephone && item.telephone !== '') {
          result = result + 'phone: ' + item.telephone + '\r\n'
        }
        if (item.mobile && item.mobile !== '') {
          result = result + 'mobile: ' + item.mobile + '\r\n'
        }
      }
      result = result + ' ' + this.availabilityStatus(item) + '\r\n'
      return result
    },
    setSearchFocus: function () {
      let that = this
      this.$nextTick(() => {
        if (that.$refs.searchInput && that.$refs.searchInput.focus) {
          that.$refs.searchInput.focus()
        }
      })
    },
    onCopy: function (type, data) {
      let that = this
      this.$copyText(data).then(function () {
        that.$buefy.toast.open(
          {
            duration: 2500,
            message: type + ' copied to clipboard',
            position: 'is-top',
            type: 'is-info'
          })
      }, function () {
        that.$buefy.toast.open(
          {
            duration: 2500,
            message: 'Could not copy ' + type + ' to clipboard',
            position: 'is-top',
            type: 'is-warning'
          })
      })
    }
  },
  watch: {
    inFocus: function (newValue) {
      if (newValue === true) {
        this.setSearchFocus()
      }
    },
    selectedAgent: function (newValue) {
      if (newValue) {
        this.recentContactsOpen = false
        this.searchResultsOpen = false
        this.browsePanelCollapsed = true
      }
    },
    selectedDepartment: function () {
      // The agent list rolls up once an agent is picked and nothing reopens it,
      // so switching department left the new department's agents out of reach.
      // nextTick so this lands after the availableValues watcher refilters.
      this.$nextTick(() => {
        if (this.$refs.agentSelector) {
          this.$refs.agentSelector.expandOptions()
        }
      })
    },
    searchQuery: function (newValue, oldValue) {
      //Nothing typed: back to the resting state, which is the recent contacts open and the results
      //closed - the same thing loadRecentContacts sets on arrival.
      //Clearing the box is the reset, and it now clears the SELECTION too. It used to leave the
      //chosen person and department sitting there with an empty search, so the panel looked reset
      //and was not - Garrett, 2026-09-16: "my point is this reset button. Doesn't reset it."
      if (!newValue || !newValue.trim()) {
        this.searchResultsOpen = false
        this.recentContactsOpen = this.recentContacts.length > 0
        this.highlightedResultIndex = -1
        this.selectedAgent = null
        this.selectedDepartment = null
        this.browsePanelCollapsed = true
        return
      }
      //Typing hides the recent contacts outright rather than narrowing them. It used to keep the
      //panel open and filter the hundred down as you typed, which put two competing lists on screen
      //and made a name that was not among the recents look like no match at all - Garrett,
      //2026-09-16: "this should be hidden, because watch what's happening. I start typing, and it's
      //narrowing 100."
      if (newValue !== oldValue) {
        this.browsePanelCollapsed = true
        this.recentContactsOpen = false
        this.searchResultsOpen = true
        //The result set has just changed underneath it, so a kept index would point at a different
        //person than the one that was highlighted. Back to "first result" for the next Enter.
        this.highlightedResultIndex = -1
      }
    }
  },
  mounted: async function () {
    if (this.inFocus) {
      this.setSearchFocus()
    }
    await this.loadRecentContacts()
  },
  beforeDestroy () {
    axios.cancel('user-last-contact-search')
    this.stopMagnifier()
  }
}
</script>

<style scoped>
.call-log, .call-log .view-container, .call-log .card-content {
  overflow: visible !important;
  max-height: none !important;
}

.call-log .view-container.box {
  height: auto !important;
}

.card-content {
  padding: 0;
}

.call-log .box {
  padding: 0.75rem;
}

.call-log .columns:not(:last-child) {
  margin-bottom: 1rem;
}

/* card-header-title is display:flex, so the selected-name span would sit
   inline with the subtitle and get squeezed once the ext/phone extras are
   appended. Stacking keeps the full value readable at any length. */
.call-log .card-header-title.stacked-heading {
  flex-direction: column;
  align-items: flex-start;
}

/* height is deliberately not set here: the selector's own scoped rule
   (.instance .button) pins it to 3.3em with !important, so an override
   would silently die on the dept/agent buttons while still applying to
   the recent-contacts buttons - leaving the two visually mismatched.
   Shrinking the font shrinks both consistently, since 3.3em is relative
   to the element's own font-size. */
/* Loosened back from 0.75rem/0.45em, which was squeezed in to fit more on screen and went too far -
   Garrett, 2026-09-16: "something got a little jammed here. We took spacing out here or something...
   I'll put it back. What a spacing between them." The buttons are searchable, so packing them
   tightly buys little; the margin is what separates neighbours that used to run together.
   The .modal-card qualifier is load-bearing: the selector's .buttons.has-addons makes Bulma fuse the
   buttons into one strip (margin-right:-1px, squared inner corners), and main.scss - which imports
   Bulma - loads after component styles, so at equal specificity Bulma won and the margin never
   showed. The radius is Bulma's own $radius, put back now the buttons stand apart. */
.call-log.modal-card ::v-deep .boption.button {
  font-size: 0.85rem;
  padding-left: 0.7em;
  padding-right: 0.7em;
  margin-right: 0.35rem;
  margin-bottom: 0.35rem;
  border-radius: 3px;
}

.call-log ::v-deep .button-selector-label {
  margin-bottom: 0.5rem;
}

/* The row Down/Up has walked to, before Enter commits it. Deliberately lighter than Buefy's own
   is-selected so the two are distinguishable when both are on screen. */
.call-log ::v-deep tr.keyboard-highlight td {
  background-color: #f0f4ff;
}

/* Blue means dialable, which is the convention people already read from their phone - Garrett,
   2026-09-16: "that's what people think of when they see, like, iPhone. Blue is, like, click to
   dial." So the digits are blue on both sides of the separator and nothing else is: colouring the
   dash or the word EXT would suggest they do something. */
.call-log ::v-deep .dir-dialable {
  color: #2e63b8;
}

/* Explicit, not inherit: in the Contact Type / Agent titles the whole value sits in a has-text-link
   span, so inherit turned the separator and the word EXT blue as well and the value read as one
   colour. #4b6271 is the label colour main.scss uses in view-containers. */
.call-log ::v-deep .dir-ext-label {
  color: #4b6271;
}

/* The department number is the click-to-call component's own link - same dialable blue as the rest. */
.call-log ::v-deep .selected-value-display .ctc-trigger a {
  color: #2e63b8;
}

/* ClickToCallComponent's root is a block div, which broke the department's selected value onto three
   lines (name / number / ext) inside a label pinned at 22px high, spilling over the buttons below.
   Inline here keeps it on the one line the label has room for; its menu is position:fixed, so it
   still opens clear of the row. */
.call-log ::v-deep .selected-value-display .ctc-dropdown {
  display: inline-block;
}

/* The lens is positioned against this root in the component's own coordinates, not position:fixed -
   the quick view it opens in may be transformed, which would make fixed relative to it anyway. */
.directory-root {
  position: relative;
}

.directory-magnifier {
  font-size: 0.75rem;
}

/* Hidden by visibility, not display, while the cursor is off the panel: the copy inside has to stay
   laid out, or the scroll offsets rebuildLens sets on it would be dropped. */
.directory-lens {
  position: absolute;
  z-index: 30;
  visibility: hidden;
  overflow: hidden;
  border-radius: 50%;
  border: 2px solid #2e63b8;
  background-color: #fff;
  box-shadow: 0 4px 14px rgba(10, 10, 10, 0.3);
  pointer-events: none;
}

.directory-lens-content {
  position: absolute;
  top: 0;
  left: 0;
  transform-origin: 0 0;
}
</style>

<style>
/* Unscoped on purpose: v-tooltip mounts its tooltips on body, where a scoped rule cannot reach. The
   body class exists only while the directory magnifier is on. */
body.directory-magnifier-on .vue-tooltip-theme {
  display: none !important;
}
</style>
